import {Func, UninstantiatedClass} from '../util';
import {ResultCallbacks} from './Alt';

/**
 * Given a function-like type append a `defer` property
 * which is a function taking the same parameters as the given type
 * but returning void.
 *
 * Used for actions defined by alt
 */
export type Deferable<Type extends Func> = Type & {
  /**
   * Deferring an action means that it doesn't try to immediately resolve
   * this prevents issues with alt's dispatcher attempting to dispatch
   * multiple actions at the same time.
   *
   * As a result, however the return type is completely abandoned
   */
  defer: (...args: Parameters<Type>) => void;
};

/**
 * Appends the `defer` method to all function-like properties of the given type
 *
 * @link Deferable
 */
export type DeferAll<Type> = {
  [Property in keyof Type]: Type[Property] extends Func
    ? Deferable<Type[Property]>
    : Type[Property];
};

/**
 * Wraps the return value given function-like type with a dispatch callback
 *
 * (...args: ArgType) => ReturnType
 *
 * will become
 *
 * (...args: ArgType) => (dispatch: any) => ReturnType
 */
type Dispatched<Type extends Func> = Parameters<Type> extends [any, ...any]
  ? (...args: Parameters<Type>) => (dispatch: (args: any) => void) => any
  : () => (dispatch: (args: any) => void) => any;

/**
 * Wraps all return values of function-like property on a class or object with
 * a dispatch callback. Used for internal implementation of Alt actions.
 *
 * See: Dispatched
 */
export type DispatchAll<Type> = {
  [Property in keyof Type]: Type[Property] extends Func
    ? Dispatched<Type[Property]>
    : Type[Property];
};

/**
 * Class used as an identifier for Action classes returned by `createActions`
 * so methods which take these as a parameter can be typed
 *
 * @link Store.bindActions
 * @link AltInterface.createActions
 */
export class Actions {}

/**
 * Defines the default methods provided by alt's `createActions` which are
 * available to action classes
 */
export interface ActionsMethods<Methods> {
  generateActions(...actions: (keyof Methods)[]): void;
}

/**
 * Defines the structure of an Actions class based on given
 * Async Methods. The returned type is a class with `Success` and `Error`
 * variants defined for all given methods as well as default methods provided
 * by alt's `createActions` such as `generateActions`
 *
 * @link ResultCallbacks
 * @link ActionsMethods
 */
export type ActionsBaseType<AsyncMethods, SyncMethods> = UninstantiatedClass<
  ResultCallbacks<AsyncMethods> &
    ActionsMethods<ResultCallbacks<AsyncMethods> & AsyncMethods & SyncMethods>
>;

/**
 * A class with no functions.
 *
 * You can use this when, for example, your ActionsBase is has no async methods.
 */
export class NoMethods {}

/**
 * Extend this when defining an Actions class
 *
 * Creates a class with the desired props casted unto it.
 * Alt defines a bunch of stuff at runtime that we can't
 * type using conventional methods so this is a workaround.
 */
export function ActionsBase<AsyncMethods, SyncMethods>(): ActionsBaseType<
  AsyncMethods,
  SyncMethods
> {
  return class {} as any;
}
